import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Globe2, Truck, Package } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { useLanguage } from '@/hooks/useLanguage';

const f = { initial:{opacity:0,y:24}, whileInView:{opacity:1,y:0}, viewport:{once:true} };

const minerals = [
  {
    id:'aluminum', icon:'🏗️',
    color:'border-blue-300', bg:'bg-blue-50', accent:'bg-blue-600',
    img: '/images/aluminum_ingot_1.jpeg',
    name:'Aluminum', nameTh:'อะลูมิเนียม',
    grade:'A7 / A00 Ingots · Alumina · Billets · Coils',
    origin:'Vietnam · Africa (Guinea) · Central Asia · Russia',
    originTh:'เวียดนาม · แอฟริกา (กินี) · เอเชียกลาง · รัสเซีย',
    desc:'Integrated mining-to-delivery platform. 3.1bn ton reserves in Vietnam alone. 3–7 day shipping to major ports.',
    descTh:'แพลตฟอร์มครบวงจร สำรอง 3.1 พันล้านตันในเวียดนาม ส่งถึงท่าเรือหลักใน 3-7 วัน',
    specs:['A7/A00 Aluminum Ingots','Alumina (Aluminum Oxide)','Aluminum Billets','Aluminum Coils & Sheets'],
  },
  {
    id:'copper', icon:'🔶',
    color:'border-orange-300', bg:'bg-orange-50', accent:'bg-orange-600',
    img: '/images/real_copper_sheets.webp',
    name:'Copper', nameTh:'ทองแดง',
    grade:'Grade A Cathodes · Wire Rods · Concentrates',
    origin:'Southeast Asia · Africa · South America',
    originTh:'เอเชียตะวันออกเฉียงใต้ · แอฟริกา · อเมริกาใต้',
    desc:'LME-registered Grade A cathodes, wire rods and concentrates. Full SGS inspection. Essential for electronics & renewable energy.',
    descTh:'แคโทด Grade A ที่ขึ้นทะเบียน LME ลวดทองแดง และคอนเซนเทรต ตรวจสอบ SGS ครบถ้วน',
    specs:['Grade A Copper Cathodes (LME)','Copper Wire Rods','Copper Concentrates','Blister Copper'],
  },
  {
    id:'tungsten', icon:'⚙️',
    color:'border-slate-300', bg:'bg-slate-50', accent:'bg-slate-700',
    img: '/images/real_copper_sheets.webp',
    name:'Tungsten', nameTh:'ทังสเตน',
    grade:'APT · Oxide · Carbide Powder · Metal',
    origin:'Southeast Asia · Central Asia',
    originTh:'เอเชียตะวันออกเฉียงใต้ · เอเชียกลาง',
    desc:'Critical industrial metal. APT, WO₃, carbide powder and metal rods for cutting tools, aerospace, defense and energy sectors.',
    descTh:'โลหะอุตสาหกรรมที่สำคัญ APT WO₃ ผงคาร์ไบด์ และแท่งโลหะสำหรับเครื่องมือตัด อวกาศ กลาโหม และพลังงาน',
    specs:['Ammonium Paratungstate (APT)','Tungsten Trioxide (WO₃)','Tungsten Carbide Powder','Tungsten Metal Rods & Bars'],
  },
  {
    id:'other', icon:'💎',
    color:'border-purple-300', bg:'bg-purple-50', accent:'bg-purple-700',
    img: '/images/mining_3.jpeg',
    name:'Other Minerals', nameTh:'แร่ธาตุอื่นๆ',
    grade:'Iron Ore · Manganese · Nickel · Bauxite',
    origin:'Africa · Southeast Asia · Global Sourcing',
    originTh:'แอฟริกา · เอเชียตะวันออกเฉียงใต้ · การจัดซื้อทั่วโลก',
    desc:'Diversified portfolio of industrial minerals. Flexible sourcing to match client-specific grade, volume, and delivery needs.',
    descTh:'พอร์ตโฟลิโอที่หลากหลายของแร่อุตสาหกรรม การจัดซื้อที่ยืดหยุ่นตามเกรด ปริมาณ และความต้องการการส่งมอบ',
    specs:['Iron Ore (62% Fe+)','Manganese Ore','Nickel Ore & Concentrates','Bauxite (Raw & Calcined)'],
  },
];

const supplyChain = [
  { step:'01', en:'Mining & Extraction', th:'การทำเหมืองและการขุดเจาะ', icon:'⛏️',
    desc:'Own or partner-operated mines in Vietnam, Africa, and Southeast Asia. Full environmental compliance.' },
  { step:'02', en:'Processing & Refining', th:'การแปรรูปและการกลั่น', icon:'🏭',
    desc:'Integrated refining and smelting. Product specs tailored to buyer requirements with strict QC.' },
  { step:'03', en:'Global Sourcing & Trading', th:'การจัดซื้อและการค้าทั่วโลก', icon:'🌐',
    desc:'Direct procurement from Central Asia, Russia, Africa, South America. Spot and long-term contracts.' },
  { step:'04', en:'Quality Assurance', th:'การประกันคุณภาพ', icon:'✅',
    desc:'Triple defense: in-house lab → SGS/CCIC at loading port → destination re-inspection. Full batch traceability.' },
  { step:'05', en:'Logistics & Delivery', th:'โลจิสติกส์และการจัดส่ง', icon:'🚢',
    desc:'50k–100k MT/month. Key ports: Haiphong, Vung Tau, Ho Chi Minh + European distribution hubs.' },
  { step:'06', en:'Financial & Compliance', th:'การเงินและการปฏิบัติตาม', icon:'📋',
    desc:'ICC-compliant docs. L/C, T/T, multi-currency settlement. LME/SHFE pricing. Performance Bond available.' },
];

const advantages = [
  { icon:'💎', title:'Resource Strength', th:'ความแข็งแกร่งด้านทรัพยากร',
    en:'Physical mining assets in Vietnam and Africa, supplemented by deep procurement networks.' },
  { icon:'🔗', title:'Full-Chain Integration', th:'การบูรณาการห่วงโซ่ครบวงจร',
    en:'Single-window management from mine to customer. Full traceability, no multi-party delays.' },
  { icon:'⚙️', title:'Flexible Terms', th:'เงื่อนไขที่ยืดหยุ่น',
    en:'FOB, CIF, CFR Incoterms. L/C and T/T. Annual, spot, or project-dedicated supply models.' },
  { icon:'🛡️', title:'Risk Management', th:'การบริหารความเสี่ยง',
    en:'Multi-origin backup. Alternative sources activated within 2–4 weeks. Safety stock at key ports.' },
  { icon:'📋', title:'Regulatory Compliance', th:'การปฏิบัติตามกฎระเบียบ',
    en:'Full ICC compliance. SGS/CCIC certified. Documentation for bank audit and due diligence.' },
  { icon:'🌏', title:'Global Market Access', th:'การเข้าถึงตลาดระดับโลก',
    en:'China, Japan, South Korea, ASEAN, Middle East, Europe via established logistics corridors.' },
];

export default function MineralsBusiness() {
  const { t, lang } = useLanguage();
  return (
    <Layout>
      {/* ── HERO with overlay ── */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <img src="/images/bulk_1.jpeg" alt="Global bulk trade"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <motion.div initial={{opacity:0,y:-16}} animate={{opacity:1,y:0}} transition={{duration:0.5}}
              className="inline-flex items-center gap-2 bg-accent/20 border border-accent/30 text-accent text-sm font-semibold px-4 py-2 rounded-full mb-6">
              💎 {t('Non-ferrous Metals & Bulk Commodities', 'โลหะอโลหะและสินค้าโภคภัณฑ์')}
            </motion.div>
            <motion.h1 initial={{opacity:0,y:24}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.1}}
              className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4">
              {lang==='th' ? (
                <>แร่ธาตุและโลหะ<br /><span className="text-accent">ระดับโลก</span></>
              ) : (
                <>Global Minerals<br /><span className="text-accent">&amp; Metals Trading</span></>
              )}
            </motion.h1>
            <motion.p initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.2}}
              className="text-white/80 text-lg mb-8">
              {t(
                'Aluminum · Copper · Tungsten · And More — From Mine to Market',
                'อะลูมิเนียม · ทองแดง · ทังสเตน · และอื่นๆ — จากเหมืองสู่ตลาด'
              )}
            </motion.p>
            <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{duration:0.5,delay:0.3}}
              className="flex flex-wrap gap-4">
              <Link to="/#/contact"
                className="inline-flex items-center gap-2 bg-accent text-slate-900 font-bold px-7 py-3.5 rounded-full hover:opacity-90 transition-all shadow-lg">
                {t('Get a Quote', 'รับใบเสนอราคา')} <ArrowRight className="w-4 h-4"/>
              </Link>
              <a href="#minerals"
                className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">
                {t('View Products', 'ดูผลิตภัณฑ์')}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Intro Stats Banner ── */}
      <section className="bg-primary py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { v:'50k–100k MT', l:'Monthly Capacity', th:'กำลังส่งออกรายเดือน' },
              { v:'4 Categories', l:'Mineral Products', th:'หมวดผลิตภัณฑ์แร่' },
              { v:'SGS / CCIC', l:'Certified Quality', th:'คุณภาพที่ได้รับการรับรอง' },
              { v:'6 Regions', l:'Global Market Reach', th:'การเข้าถึงตลาดโลก' },
            ].map((s,i) => (
              <motion.div key={s.l} {...f} transition={{delay:i*0.07}}
                className="text-primary-foreground">
                <div className="text-2xl font-bold text-accent">{s.v}</div>
                <div className="text-primary-foreground/70 text-xs mt-0.5">{t(s.l, s.th)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Photo Gallery Strip ── */}
      <section className="py-6 bg-slate-900 overflow-hidden">
        <div className="flex gap-3 px-4 overflow-x-auto scrollbar-hide">
          {[
            { img:'/images/real_copper_sheets.webp', cap:'Copper Plates' },
            { img:'/images/real_copper_sheets.webp', cap:'Copper — Zambia' },
            { img:'/images/aluminum_ingot_1.jpeg', cap:'Metal Products' },
            { img:'/images/mining_6.jpeg', cap:'Open Pit Mine' },
            { img:'/images/mining_8.jpeg', cap:'Mining Site' },
            { img:'/images/bulk_10.jpeg', cap:'Haiphong Port' },
            { img:'/images/bulk_5.jpeg', cap:'Sea Freight' },
            { img:'/images/bulk_2.jpeg', cap:'Container Ship' },
          ].map((p,i) => (
            <div key={i} className="flex-shrink-0 w-48 h-32 rounded-xl overflow-hidden relative group">
              <img src={p.img} alt={p.cap} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-2 left-2 text-white text-xs font-semibold">{p.cap}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Four Minerals (id anchor) ── */}
      <section id="minerals" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-14">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('Our Mineral Portfolio', 'พอร์ตโฟลิโอแร่ธาตุของเรา')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">
              {t('Four Core Categories', 'สี่หมวดหลัก')}
            </h2>
          </motion.div>

          <div className="space-y-10">
            {minerals.map((m,i) => (
              <motion.div key={m.id} {...f} transition={{delay:i*0.08}}
                className={`rounded-2xl border-2 ${m.color} overflow-hidden shadow-lg`}>
                <div className={`grid grid-cols-1 ${i%2===0 ? 'lg:grid-cols-[2fr_3fr]' : 'lg:grid-cols-[3fr_2fr]'}`}>
                  {/* Image side */}
                  <div className={`${i%2===0 ? 'order-1' : 'order-1 lg:order-2'} h-64 lg:h-auto overflow-hidden`}>
                    <img src={m.img} alt={t(m.name, m.nameTh)}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                  </div>
                  {/* Content side */}
                  <div className={`${m.bg} p-7 flex flex-col justify-center ${i%2===0 ? 'order-2' : 'order-2 lg:order-1'}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`${m.accent} text-white w-10 h-10 rounded-xl flex items-center justify-center text-xl`}>{m.icon}</div>
                      <div>
                        <h3 className="text-2xl font-bold text-foreground">{t(m.name, m.nameTh)}</h3>
                        <p className="text-xs font-mono text-primary">{m.grade}</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{t(m.desc, m.descTh)}</p>
                    <div className="text-xs text-muted-foreground mb-5">
                      <span className="font-semibold text-foreground">{t('Origin: ','แหล่งที่มา: ')}</span>
                      {t(m.origin, m.originTh)}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {m.specs.map(sp => (
                        <div key={sp} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0"/>
                          <span className="text-foreground">{sp}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Logistics & Shipping Visual ── */}
      <section className="py-20 bg-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-12">
            <span className="text-accent font-semibold text-sm uppercase tracking-widest">
              {t('Global Logistics', 'โลจิสติกส์ระดับโลก')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">
              {t('From Mine to Your Port', 'จากเหมืองสู่ท่าเรือของคุณ')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Left: big shipping photo */}
            <motion.div {...f} className="rounded-2xl overflow-hidden h-80">
              <img src="/images/bulk_10.jpeg" alt="Haiphong Port Vietnam"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            {/* Right: two stacked photos */}
            <div className="grid grid-rows-2 gap-4 h-80">
              <motion.div {...f} transition={{delay:0.1}} className="rounded-2xl overflow-hidden">
                <img src="/images/bulk_6.jpeg" alt="Rotterdam port"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div {...f} transition={{delay:0.15}} className="rounded-2xl overflow-hidden">
                <img src="/images/bulk_2.jpeg" alt="Container ship"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon:<Truck className="w-5 h-5"/>, t:'Key Ports', th:'ท่าเรือหลัก',
                d:'Haiphong · Vung Tau · Ho Chi Minh City (SE Asia) + European hubs' },
              { icon:<Package className="w-5 h-5"/>, t:'Monthly Capacity', th:'กำลังรายเดือน',
                d:'50,000 – 100,000 MT / month, scalable by contract volume' },
              { icon:<Shield className="w-5 h-5"/>, t:'Quality Standards', th:'มาตรฐานคุณภาพ',
                d:'SGS / CCIC certified per batch · LME / SHFE price reference' },
            ].map((c,i) => (
              <motion.div key={c.t} {...f} transition={{delay:i*0.08}}
                className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-2 text-accent">{c.icon}
                  <span className="font-bold text-white text-sm">{t(c.t, c.th)}</span>
                </div>
                <p className="text-slate-300 text-sm">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Supply Chain Steps ── */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-12">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('How We Work', 'วิธีที่เราทำงาน')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">
              {t('Full-Chain Supply Process', 'กระบวนการจัดหาแบบครบวงจร')}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {supplyChain.map((s,i) => (
              <motion.div key={s.step} {...f} transition={{delay:i*0.08}}
                className="bg-white rounded-2xl p-6 shadow-sm border border-border/50 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">{s.step}</div>
                  <span className="text-2xl">{s.icon}</span>
                </div>
                <h3 className="font-bold text-foreground mb-1">{t(s.en, s.th)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mining Visuals ── */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-8">
            <h3 className="text-xl font-bold text-foreground">{t('Our Mining Assets', 'สินทรัพย์เหมืองแร่ของเรา')}</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {t('Vietnam, Africa, Southeast Asia — Verified Reserves', 'เวียดนาม แอฟริกา เอเชียตะวันออกเฉียงใต้ — สำรองที่ผ่านการตรวจสอบ')}
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { img:'/images/mining_1.jpeg', cap:'Sand Quarry' },
              { img:'/images/mining_3.jpeg', cap:'Open Pit Mine' },
              { img:'/images/mining_6.jpeg', cap:'Aerial Mine View' },
              { img:'/images/mining_8.jpeg', cap:'Mineral Extraction' },
            ].map((p,i) => (
              <motion.div key={i} {...f} transition={{delay:i*0.08}}
                className="relative rounded-2xl overflow-hidden h-44 group shadow-md">
                <img src={p.img} alt={p.cap} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 text-white text-xs font-semibold">{p.cap}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Advantages ── */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-12">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('Why Choose Yaruwat', 'ทำไมต้องเลือก Yaruwat')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">
              {t('Six Strategic Advantages', 'ข้อได้เปรียบเชิงกลยุทธ์หกประการ')}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((a,i) => (
              <motion.div key={a.title} {...f} transition={{delay:i*0.08}}
                className="bg-white rounded-2xl p-6 shadow-md border border-border/50 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">{a.icon}</div>
                <h3 className="font-bold text-foreground mb-2">{t(a.title, a.th)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.en}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cooperation Models ── */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground">{t('Cooperation Models', 'รูปแบบความร่วมมือ')}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon:'📅', t:'Annual Framework', th:'กรอบรายปี', d:'Stable annual supply with fixed or floating price formula. Ideal for long-term partnerships.' },
              { icon:'📦', t:'Flexible Spot', th:'การซื้อขายแบบยืดหยุ่น', d:'Market-based negotiation per shipment. Great for spot purchasing and trial orders.' },
              { icon:'🏗️', t:'Project Dedicated', th:'เฉพาะโครงการ', d:'Tailored supply plan for infrastructure or industrial projects with dedicated logistics.' },
            ].map((m,i) => (
              <motion.div key={m.t} {...f} transition={{delay:i*0.1}}
                className="bg-white rounded-2xl p-6 shadow-md border border-border/50 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">{m.icon}</div>
                <h3 className="font-bold text-foreground text-lg mb-2">{t(m.t, m.th)}</h3>
                <p className="text-sm text-muted-foreground">{m.d}</p>
              </motion.div>
            ))}
          </div>

          {/* Target clients */}
          <motion.div {...f} transition={{delay:0.2}} className="mt-8 bg-muted/50 rounded-2xl p-7">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <Globe2 className="w-5 h-5 text-primary"/>
              {t('Who We Serve', 'ลูกค้าที่เราให้บริการ')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon:'🏢', e:'Global Trading Houses', th:'บริษัทการค้าระดับโลก' },
                { icon:'🏭', e:'MNC Manufacturing', th:'การผลิตข้ามชาติ' },
                { icon:'🔧', e:'Processing Enterprises', th:'องค์กรแปรรูป' },
                { icon:'🏛️', e:'State-Owned Metals Cos', th:'บริษัทโลหะรัฐวิสาหกิจ' },
              ].map(c => (
                <div key={c.e} className="flex items-center gap-2 bg-white rounded-xl p-3 shadow-sm border border-border/50">
                  <span className="text-xl">{c.icon}</span>
                  <span className="text-xs font-medium text-foreground">{t(c.e, c.th)}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Vision ── */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...f}>
            <h2 className="text-3xl font-bold mb-4">{t('Our Vision', 'วิสัยทัศน์ของเรา')}</h2>
            <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-10">
              {t(
                'To become an integrated multinational mineral resource and service platform — rooted in Southeast Asia and Africa, reaching the world.',
                'เป็นแพลตฟอร์มทรัพยากรแร่ธาตุและบริการข้ามชาติแบบบูรณาการ มีรากฐานในเอเชียตะวันออกเฉียงใต้และแอฟริกา เข้าถึงโลก'
              )}
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { n:'1', icon:'🏭', t:'Expand Capacity', th:'ขยายกำลัง' },
              { n:'2', icon:'🌐', t:'Strengthen Network', th:'เสริมเครือข่าย' },
              { n:'3', icon:'⬆️', t:'Extend Products', th:'ขยายผลิตภัณฑ์' },
              { n:'4', icon:'💳', t:'Integrate Finance', th:'บูรณาการการเงิน' },
            ].map((s,i) => (
              <motion.div key={s.n} {...f} transition={{delay:i*0.1}}
                className="bg-primary-foreground/10 rounded-2xl p-5 text-center border border-primary-foreground/20">
                <div className="text-3xl mb-2">{s.icon}</div>
                <div className="font-bold text-sm">{t(s.t, s.th)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div {...f}>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              {t('Ready to Partner in Global Minerals?', 'พร้อมที่จะเป็นพันธมิตรด้านแร่ธาตุระดับโลกหรือยัง?')}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {t(
                'Contact us today — first-order offer within one week. FOB, CIF, CFR options available.',
                'ติดต่อเราวันนี้ — ราคาเสนอแรกภายในหนึ่งสัปดาห์ มีตัวเลือก FOB, CIF, CFR'
              )}
            </p>
            <Link to="/#/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-8 py-4 rounded-full hover:opacity-90 transition-all shadow-lg">
              {t('Start a Conversation', 'เริ่มการสนทนา')} <ArrowRight className="w-5 h-5"/>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
